import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiKeyLight, PiLockSimpleLight } from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import GlassCard from '../../shared/brand/GlassCard';
import { FOCUS_RING_ONLY, SELECTED_TINT } from '../../shared/TailwindComponents/FieldShell';
import sectionIcon from './sectionIcons';
import {
  PermissionSection,
  UserPermission,
  capitalizeSection,
  directPermissionIds,
  visiblePermissionSections,
} from './userEditModel';

export interface UserPermissionsPanelProps {
  sections: PermissionSection[];
  /** The full EFFECTIVE set, each entry tagged `direct` or `role`. */
  userPermissions: UserPermission[];
  orgType?: string | null;
  /**
   * Only an org admin may write. Everyone else - including the person looking
   * at their own record - gets the same grid with no Edit button, because the
   * backend 403s them anyway and a control that always fails is worse than no
   * control.
   */
  canEdit?: boolean;
  /**
   * Save the COMPLETE desired set of direct permission ids. The page owns the
   * write; resolving what the user now holds is the response's job.
   */
  onSave?: (permissionIds: number[]) => Promise<void>;
}

/**
 * Every permission this person could hold, grouped by section.
 *
 * A permission that comes from a role is shown locked rather than hidden - an
 * admin looking for "why can they see Matters?" needs to be told the role
 * grants it, not left with a gap where the answer should be. It is also not
 * editable here: unpicking it would silently diverge from the role that gave
 * it, and the endpoint only takes direct grants.
 *
 * Editing is a MODE, not a per-toggle write. The endpoint takes the whole
 * desired set in one PUT, so the grid collects a draft and saves it once —
 * which is also what makes "revoke three, grant one" a single auditable change
 * rather than four requests that can half-fail.
 */
const UserPermissionsPanel: React.FC<UserPermissionsPanelProps> = ({
  sections,
  userPermissions,
  orgType,
  canEdit = false,
  onSave,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Set<number>>(new Set());

  /** What the role gives them - locked in both modes. */
  const roleGranted = useMemo(
    () => new Set((userPermissions || []).filter((p) => p.source === 'role').map((p) => p.id)),
    [userPermissions]
  );
  const savedDirect = useMemo(() => directPermissionIds(userPermissions), [userPermissions]);

  // A save re-renders from `data.current`; the draft follows it rather than
  // holding the pre-save picture.
  useEffect(() => {
    if (!editing) setDraft(new Set(savedDirect));
  }, [editing, savedDirect]);

  const granted = (id: number) => roleGranted.has(id) || (editing ? draft.has(id) : savedDirect.includes(id));
  const visible = visiblePermissionSections(sections, orgType);
  const grantedCount = editing
    ? new Set([...Array.from(roleGranted), ...Array.from(draft)]).size
    : userPermissions.length;

  const toggle = (permissionId: number) => {
    if (!editing || roleGranted.has(permissionId)) return;
    setDraft((current) => {
      const next = new Set(current);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(Array.from(draft));
      setEditing(false);
    } catch {
      // The page has already told the user what went wrong; staying in edit
      // mode keeps their draft so they can fix it or back out.
    } finally {
      setSaving(false);
    }
  };

  if (userPermissions.length === 0 && visible.length === 0) {
    return (
      <GlassCard>
        <EmptyState
          density="compact"
          icon={<PiKeyLight />}
          headline={t('No permissions assigned')}
          subtext={t('Assign a role, or grant individual permissions once they are configured.')}
        />
      </GlassCard>
    );
  }

  return (
    <GlassCard
      icon={<PiKeyLight />}
      title={t('Permissions')}
      subtitle={
        canEdit
          ? t('Role permissions are locked — edit the role to change them.')
          : t('Only an organization admin can change permissions.')
      }
      actions={
        <div className="flex items-center gap-2">
          <Badge color="gray" className="text-xs">
            {t('{{count}} granted', { count: grantedCount })}
          </Badge>
          {canEdit && onSave && (
            editing ? (
              <>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => { setEditing(false); setDraft(new Set(savedDirect)); }}
                  disabled={saving}
                >
                  {t('Cancel')}
                </Button>
                <Button variant="primary" size="small" onClick={handleSave} isLoading={saving}>
                  {t('Save')}
                </Button>
              </>
            ) : (
              <Button variant="outline" size="small" onClick={() => setEditing(true)}>
                {t('Edit')}
              </Button>
            )
          )}
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {visible.map((section) => {
          const selectedCount = section.permissions.filter((p) => granted(p.id)).length;
          return (
            <div
              key={section.id}
              className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-hairline-soft"
            >
              <div className="flex items-center justify-between gap-2 border-b border-hairline-soft bg-surface-sunken/70 px-3 py-2">
                <span className="flex min-w-0 flex-1 items-center gap-2 text-fg-strong">
                  {sectionIcon(section.name, 'h-4 w-4')}
                  <span className="truncate text-xs font-medium">
                    {capitalizeSection(section.name)}
                  </span>
                </span>
                <Badge color={selectedCount > 0 ? 'green' : 'gray'} className="flex-shrink-0 text-[10px]">
                  {selectedCount}/{section.permissions.length}
                </Badge>
              </div>

              <div className="custom-scrollbar-main max-h-48 min-h-0 space-y-1 overflow-y-auto p-2">
                {section.permissions.length === 0 ? (
                  <p className="py-2 text-xs text-fg-muted">{t('No permissions')}</p>
                ) : (
                  section.permissions.map((perm) => {
                    const isRoleBased = roleGranted.has(perm.id);
                    const hasPermission = granted(perm.id);
                    const label = perm.description || perm.name;
                    const editable = editing && !isRoleBased;
                    return (
                      <button
                        key={perm.id}
                        type="button"
                        onClick={() => toggle(perm.id)}
                        disabled={!editable}
                        aria-pressed={editable ? hasPermission : undefined}
                        className={clsx(
                          'flex w-full items-center gap-1 rounded border px-2 py-1 text-start text-xs transition-colors',
                          FOCUS_RING_ONLY,
                          !editable && 'cursor-default',
                          hasPermission && isRoleBased
                            ? 'border-hairline-soft bg-surface-sunken/70 text-fg-muted'
                            : hasPermission
                              ? SELECTED_TINT
                              : clsx(
                                  'border-hairline-soft bg-surface-sunken/40 text-fg-body',
                                  editable && 'hover:bg-surface-sunken'
                                )
                        )}
                        title={
                          isRoleBased
                            ? `${label} - ${t('from role, edit the role to change it')}`
                            : label
                        }
                      >
                        <span className="min-w-0 flex-1 truncate">{label}</span>
                        {isRoleBased && (
                          <PiLockSimpleLight className="h-3 w-3 flex-shrink-0 opacity-60" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
};

export default UserPermissionsPanel;
